import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { adminCanAccess } from '../lib/adminAccess';
import { ADMIN_NAV_ITEMS, isAdminMorePath } from '../lib/adminNav';
import { scrollWindowToTop } from './ScrollToTop';
import { navDirection, setNavDirection } from '../lib/motion';
import { BrandMark } from './BrandLogo';

const PUBLIC_NAV = [
  { to: '/',        label: 'Home' },
  { to: '/clinics', label: 'Internships' },
  { to: '/doctors', label: 'Mentor' },
  { to: '/blogs',   label: 'Learn' },
  { to: '/events',  label: 'Events' },
];

/** Clinic staff: logo + Dashboard / Sign out only — no tab links. */
const CLINIC_NAV: { to: string; label: string }[] = [];

function MotionLink({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const { pathname } = useLocation();
  return (
    <Link
      to={to}
      className={className}
      onClick={(e) => {
        setNavDirection(navDirection(pathname, to));
        onClick?.(e);
      }}
    >
      {children}
    </Link>
  );
}

export function Navbar() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(() => isAdminMorePath(pathname));
  const moreRef = useRef<HTMLDivElement>(null);

  const isAdmin = me?.role === 'admin';
  const isClinic = me?.role === 'clinic_staff';
  const isDashboardOnly = isAdmin || isClinic;

  const adminAccessible = isAdmin
    ? ADMIN_NAV_ITEMS.filter(n => adminCanAccess(me, n.module))
    : [];
  const adminPrimary = adminAccessible.filter(n => n.group === 'primary');
  const adminMore = adminAccessible.filter(n => n.group === 'more');

  useEffect(() => {
    if (isAdminMorePath(pathname)) setMobileMoreOpen(true);
  }, [pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moreOpen]);

  const handleLogout = async () => {
    setOpen(false);
    setMoreOpen(false);
    await logout();
    navigate('/login');
  };

  const profileHref =
    !me                      ? '/' :
    me.role === 'admin'      ? '/admin' :
    me.role === 'clinic_staff' ? '/clinic' :
    me.role === 'doctor'     ? '/doctor' :
    '/student/profile';

  const profileLabel = isDashboardOnly ? 'Dashboard' : me?.role === 'student' ? 'Profile' : 'Profile';
  const logoHref = isAdmin ? '/admin' : isClinic ? '/clinic' : '/';
  const navLinks = isAdmin
    ? adminPrimary.map(n => ({ to: n.href, label: n.label }))
    : isClinic
      ? CLINIC_NAV
      : me?.role === 'student'
        ? [
            { to: '/student', label: 'Club' },
            { to: '/clinics', label: 'Internships' },
            { to: '/doctors', label: 'Mentor' },
            { to: '/blogs', label: 'Learn' },
            { to: '/events', label: 'Events' },
          ]
        : PUBLIC_NAV;
  const overviewPath = isAdmin ? '/admin' : isClinic ? '/clinic' : null;
  const moreActive = isAdmin && isAdminMorePath(pathname);

  // Clinic home shell has its own sidebar/topbar; keep global nav for management/onboarding.
  if (isClinic && (pathname === '/clinic' || pathname === '/clinic/')) {
    return null;
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <nav className="site-nav bg-teal border-b border-line sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <MotionLink
            to={logoHref}
            className="flex items-center gap-2 min-w-0 shrink-0"
            onClick={(e) => {
              setOpen(false);
              if (pathname === logoHref) {
                e.preventDefault();
                scrollWindowToTop();
              }
            }}
          >
            <span className="shrink-0 rounded-lg bg-white p-[3px] leading-none">
              <BrandMark />
            </span>
            <span className="font-display text-2xl sm:text-[1.75rem] leading-none text-white tracking-tight truncate">
              Physio<span className="text-sand">EDGE</span>
            </span>
            {isAdmin && (
              <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-white/50">
                Admin
              </span>
            )}
            {isClinic && (
              <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-white/50">
                Clinic
              </span>
            )}
          </MotionLink>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-8 min-w-0">
            {isAdmin ? (
              <>
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-none max-w-[min(100%,42rem)]">
                  {adminPrimary.map(link => {
                    const active =
                      pathname === link.href ||
                      (link.href !== '/admin' && pathname.startsWith(link.href));
                    return (
                      <MotionLink
                        key={link.href}
                        to={link.href}
                        className={`shrink-0 text-sm px-2.5 py-1.5 rounded-radius-sm transition-colors ${
                          active ? 'bg-white/15 text-white font-medium' : 'text-teal-soft hover:text-white'
                        }`}
                      >
                        {link.label}
                      </MotionLink>
                    );
                  })}
                </div>
                {adminMore.length > 0 && (
                  <div ref={moreRef} className="relative shrink-0">
                    <button
                      type="button"
                      onClick={() => setMoreOpen(o => !o)}
                      className={`text-sm px-2.5 py-1.5 rounded-radius-sm transition-colors ${
                        moreActive || moreOpen
                          ? 'bg-white/15 text-white font-medium'
                          : 'text-teal-soft hover:text-white'
                      }`}
                      aria-expanded={moreOpen}
                    >
                      More
                    </button>
                    {moreOpen && (
                      <div className="absolute right-0 top-full mt-2 z-50 min-w-[11rem] rounded-radius-sm border border-line bg-surface shadow-pop py-1">
                        {adminMore.map(link => {
                          const active =
                            pathname === link.href || pathname.startsWith(`${link.href}/`);
                          return (
                            <MotionLink
                              key={link.href}
                              to={link.href}
                              className={`block px-3 py-2 text-sm transition-colors ${
                                active ? 'bg-teal-soft text-teal font-medium' : 'text-ink-soft hover:bg-bg hover:text-ink'
                              }`}
                              onClick={() => setMoreOpen(false)}
                            >
                              {link.label}
                            </MotionLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="shrink-0 text-sm text-teal-soft hover:text-white transition-colors ml-1"
                >
                  Sign out
                </button>
              </>
            ) : isClinic ? (
              <>
                <MotionLink
                  to={logoHref}
                  className="text-sm font-medium text-white bg-teal-2 px-3 py-1.5 rounded-radius-sm hover:bg-opacity-90 transition-colors"
                >
                  Dashboard
                </MotionLink>
                <button
                  onClick={handleLogout}
                  className="text-sm text-teal-soft hover:text-white transition-colors"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-6 lg:gap-8">
                  {me?.role === 'student' && (
                    <MotionLink to="/student" className="text-sm text-teal-soft hover:text-white transition-colors">Club</MotionLink>
                  )}
                  <MotionLink to="/clinics" className="text-sm text-teal-soft hover:text-white transition-colors">Internships</MotionLink>
                  <MotionLink to="/doctors" className="text-sm text-teal-soft hover:text-white transition-colors">Mentor</MotionLink>
                  <MotionLink to="/blogs" className="text-sm text-teal-soft hover:text-white transition-colors">Learn</MotionLink>
                  <MotionLink to="/events" className="text-sm text-teal-soft hover:text-white transition-colors">Events</MotionLink>
                </div>
                {me ? (
                  <div className="flex items-center gap-5 pl-6 border-l border-white/20">
                    <MotionLink
                      to={profileHref}
                      className="text-sm font-medium text-white bg-teal-2 px-3.5 py-1.5 rounded-radius-sm hover:bg-opacity-90 transition-colors"
                    >
                      {profileLabel}
                    </MotionLink>
                    <button
                      onClick={handleLogout}
                      className="text-sm text-teal-soft hover:text-white transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-5 pl-6 border-l border-white/20">
                    <MotionLink to="/login" className="text-sm text-teal-soft hover:text-white transition-colors">Log in</MotionLink>
                    <MotionLink
                      to="/register"
                      className="text-sm font-medium text-white bg-sand px-4 py-1.5 rounded-radius-sm hover:opacity-90 transition-opacity"
                    >
                      Join us
                    </MotionLink>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile hamburger → X */}
          <button
            type="button"
            className="md:hidden text-white p-2 -mr-1 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
            onClick={() => setOpen(o => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <span className="relative block w-[18px] h-[14px]" aria-hidden>
              <span
                className={[
                  'absolute left-0 right-0 h-[2px] rounded-full bg-white origin-center',
                  'transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                  open ? 'top-[6px] rotate-45' : 'top-0 rotate-0',
                ].join(' ')}
              />
              <span
                className={[
                  'absolute left-0 right-0 top-[6px] h-[2px] rounded-full bg-white',
                  'transition-opacity duration-200 ease-out',
                  open ? 'opacity-0' : 'opacity-100',
                ].join(' ')}
              />
              <span
                className={[
                  'absolute left-0 right-0 h-[2px] rounded-full bg-white origin-center',
                  'transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                  open ? 'top-[6px] -rotate-45' : 'top-[12px] rotate-0',
                ].join(' ')}
              />
            </span>
          </button>
        </div>

        <div
          className={[
            'md:hidden absolute top-full left-0 right-0 bg-[#0A1E34] border-b border-white/10',
            'transition-all duration-200 ease-out overflow-hidden',
            open ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 pointer-events-none',
          ].join(' ')}
        >
          <nav className="py-4 px-3 space-y-1">
            {navLinks.map(link => {
              const active = pathname === link.to || (link.to !== '/' && link.to !== overviewPath && pathname.startsWith(link.to));
              const overviewActive = overviewPath != null && link.to === overviewPath && pathname === overviewPath;
              const isActive = overviewPath != null && link.to === overviewPath ? overviewActive : active;
              return (
                <MotionLink
                  key={link.to}
                  to={link.to}
                  className={[
                    'flex items-center px-3 py-3.5 rounded-radius-sm text-base font-medium transition-colors touch-manipulation min-h-[48px]',
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'text-white/80 hover:text-white hover:bg-white/10',
                  ].join(' ')}
                  onClick={(e) => {
                    setOpen(false);
                    if (pathname === link.to) {
                      e.preventDefault();
                      scrollWindowToTop();
                    }
                  }}
                >
                  {link.label}
                </MotionLink>
              );
            })}

            {isAdmin && adminMore.length > 0 && (
              <div className="pt-2 mt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setMobileMoreOpen(o => !o)}
                  className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold uppercase tracking-wide text-white/50 touch-manipulation min-h-[44px]"
                  aria-expanded={mobileMoreOpen}
                >
                  More
                  <span className="text-white/40">{mobileMoreOpen ? '−' : '+'}</span>
                </button>
                {mobileMoreOpen &&
                  adminMore.map(link => {
                    const active =
                      pathname === link.href || pathname.startsWith(`${link.href}/`);
                    return (
                      <MotionLink
                        key={link.href}
                        to={link.href}
                        className={[
                          'flex items-center px-3 py-3.5 rounded-radius-sm text-base font-medium transition-colors touch-manipulation min-h-[48px]',
                          active
                            ? 'bg-white/15 text-white'
                            : 'text-white/80 hover:text-white hover:bg-white/10',
                        ].join(' ')}
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </MotionLink>
                    );
                  })}
              </div>
            )}
          </nav>

          <div className="px-4 pb-6 pt-2 border-t border-white/10 space-y-2.5">
            {me ? (
              !isDashboardOnly ? (
                <>
                  <MotionLink
                    to={profileHref}
                    className="flex items-center justify-center text-sm font-semibold text-white bg-teal-2 px-4 py-3.5 rounded-radius-sm hover:bg-opacity-90 transition-colors touch-manipulation min-h-[48px]"
                    onClick={() => setOpen(false)}
                  >
                    {profileLabel}
                  </MotionLink>
                  <button
                    onClick={handleLogout}
                    className="w-full text-center text-sm text-white/70 hover:text-white py-3 transition-colors touch-manipulation min-h-[44px]"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <button
                  onClick={handleLogout}
                  className="w-full text-center text-sm font-medium text-white/80 hover:text-white border border-white/20 px-4 py-3.5 rounded-radius-sm transition-colors touch-manipulation min-h-[48px]"
                >
                  Sign out
                </button>
              )
            ) : (
              <>
                <MotionLink
                  to="/login"
                  className="flex items-center justify-center text-sm font-medium text-white/90 hover:text-white border border-white/25 px-4 py-3.5 rounded-radius-sm transition-colors touch-manipulation min-h-[48px]"
                  onClick={() => setOpen(false)}
                >
                  Log in
                </MotionLink>
                <MotionLink
                  to="/register"
                  className="flex items-center justify-center text-sm font-semibold text-white bg-sand px-4 py-3.5 rounded-radius-sm hover:opacity-90 transition-opacity touch-manipulation min-h-[48px]"
                  onClick={() => setOpen(false)}
                >
                  Join us
                </MotionLink>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}
